import React from "react";
import { AlertTriangle, Info, XCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * TrialBanner — shown on the dashboard for Individual plan users only.
 *
 * The free trial is card-free and measured in ANALYSIS RUNS: one agent call per
 * run, so a re-analysis spends one too. It ends at whichever comes first — the
 * run allowance or day 30 — and only then is a card requested. Nothing is ever
 * charged automatically, which is why no state here mentions billing starting.
 *
 * Props (from GET /api/v1/subscriptions/status):
 *   planTier      string  – user's plan tier, e.g. "Individual"
 *   planStatus    string  – subscription.status: "trialing" | "active" | "past_due" | ...
 *   runsUsed      number  – usage.trial_runs_used
 *   runLimit      number  – usage.trial_run_limit
 *   daysRemaining number  – usage.trial_days_remaining
 *   limitReached  boolean – usage.trial_limit_reached (runs spent or expired)
 */
const TrialBanner = ({ planTier, planStatus, runsUsed, runLimit, daysRemaining, limitReached }) => {
  const navigate = useNavigate();
  const tier = (planTier || "").toLowerCase();

  if (tier !== "individual") return null;
  if (!planStatus) return null;

  const used = Number(runsUsed) || 0;
  const limit = Number(runLimit) || 5;
  const left = Math.max(0, limit - used);
  const days = daysRemaining === null || daysRemaining === undefined ? null : Number(daysRemaining);

  // ── past_due: payment failed ──────────────────────────────────────────────
  if (planStatus === "past_due") {
    return (
      <div className="flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium leading-snug">
            Payment failed — update your card to restore full access.
          </p>
        </div>
        <button
          onClick={() => navigate("/subscription")}
          className="text-xs font-semibold text-red-600 hover:text-red-800 whitespace-nowrap underline flex-shrink-0"
        >
          Update Card
        </button>
      </div>
    );
  }

  // ── active: paid subscription ─────────────────────────────────────────────
  if (planStatus === "active") {
    return (
      <div className="flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
        <p className="text-sm text-green-700 font-semibold leading-snug">
          Subscription active — your reports and analyses are unlocked.
        </p>
      </div>
    );
  }

  // ── trialing ──────────────────────────────────────────────────────────────
  if (planStatus === "trialing") {
    // Trial spent: allowance used up or the 30 days elapsed. Reports stay readable.
    if (limitReached || left === 0 || days === 0) {
      const reason = left === 0
        ? `You have used all ${limit} analyses in your free trial.`
        : "Your 30-day free trial has ended.";
      return (
        <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800 font-medium leading-snug">
              {reason} Add a card to run new analyses — your existing reports stay available.
            </p>
          </div>
          <button
            onClick={() => navigate("/subscription")}
            className="text-xs font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap underline flex-shrink-0"
          >
            Add Card
          </button>
        </div>
      );
    }

    // Last analysis remaining.
    if (left === 1) {
      return (
        <div className="flex items-center justify-between gap-4 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <p className="text-sm text-orange-700 font-medium leading-snug">
              1 analysis left in your free trial
              {days !== null ? ` · ${days} ${days === 1 ? "day" : "days"} remaining` : ""}
              {" "}— we'll ask for a card once it's used.
            </p>
          </div>
          <button
            onClick={() => navigate("/subscription")}
            className="text-xs font-semibold text-orange-600 hover:text-orange-800 whitespace-nowrap underline flex-shrink-0"
          >
            View Plans
          </button>
        </div>
      );
    }

    // Trial running normally.
    return (
      <div className="flex items-center justify-between gap-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700 leading-snug">
            <span className="font-semibold">Free trial active</span>
            {" · "}
            {left} of {limit} analyses left
            {days !== null ? ` · ${days} ${days === 1 ? "day" : "days"} remaining` : ""}
            {" · "}
            No card needed until then
          </p>
        </div>
        <button
          onClick={() => navigate("/subscription")}
          className="text-xs font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap underline flex-shrink-0"
        >
          View Plans
        </button>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
